/*
 * File: CreateTitle.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 9, 2025 at 11:15 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';
import { stripHtml } from '../../../../utilities/utilities';

// Components
import { Typography } from '../../Typography';
import { Button } from '../../Button';
import { LocaleLink } from '../../LocaleLink';
import { TextInput } from '../../TextInput';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './CreateTitle.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const CreateTitle = forwardRef(({
	className, moveForward, moveBackward, storyTitle, setStoryTitle, context, isSubmitting, handleSubmitStory
}, ref) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.create_title, [partner]);

	// Handle next action
	const nextAction = async () => {
		if (context === 'story') {

			// Move to next step
			moveForward();

		} else {

			// Handle submit story
			await handleSubmitStory();
		}
	};

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					<Typography tag="h1" weight="black">
						{!partnerContent?.title ? (
							<Trans t={t} i18nKey="pageShareStory.createTitle.title">
								Thank you so much for sharing.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

					{/* Subtitle */}
					{context === 'story' ? (
						<Typography tag="p">
							{!partnerContent?.subtitle ? (
								<Trans t={t} i18nKey="pageShareStory.createTitle.subtitleStory">
									Would you like to name or add a title to your story?
									{' '}
									<strong>This is completely optional.</strong>
									{' '}
									We will give your story a unique number as well.
								</Trans>
							) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					) : (
						<Typography tag="p">
							{!partnerContent?.subtitle ? (
								<Trans t={t} i18nKey="pageShareStory.createTitle.subtitleUpdate">
									Would you like to name or add a title to your update?
									{' '}
									<strong>This is completely optional.</strong>
									{' '}
									We will give your update a unique number as well.
								</Trans>
							) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					)}

				</S.ContentContainer>

				{/* Inputs */}
				<S.InputContainer>
					<TextInput
						placeholder={t('pageShareStory.createTitle.placeholder')}
						name="message"
						type="text"
						value={storyTitle || ''}
						onChange={(event) => {
							const { value } = event.target;
							setStoryTitle(stripHtml(value));
						}}
					/>
				</S.InputContainer>

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" variant="text" onClick={moveBackward} disabled={isSubmitting}>
						<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
						<Typography>
							<Trans t={t} i18nKey="actions.back">
								Back
							</Trans>
						</Typography>
					</Button>
					<Button size="large" onClick={nextAction} isLoading={isSubmitting} disabled={isSubmitting}>
						<Typography>
							{context === 'story' ? (
								<Trans t={t} i18nKey="actions.continue">
									Continue
								</Trans>
							) : (
								<Trans t={t} i18nKey="pageShareStory.createTitle.shareUpdateButton">
									Share update
								</Trans>
							)}
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

			</S.InnerStep>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

CreateTitle.displayName = 'CreateTitle';
CreateTitle.propTypes = {
	className: PropTypes.string,
	moveForward: PropTypes.func,
	moveBackward: PropTypes.func,
	storyTitle: PropTypes.string,
	setStoryTitle: PropTypes.func,
	context: PropTypes.string,
	isSubmitting: PropTypes.bool,
	handleSubmitStory: PropTypes.func
};
CreateTitle.defaultProps = {
	className: null,
	moveForward: null,
	moveBackward: null,
	storyTitle: null,
	setStoryTitle: null,
	context: 'story',
	isSubmitting: false,
	handleSubmitStory: null
};
